import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getDomainById, DOMAINS } from '../../../shared/constants/domains';
import DomainBadge from '../../../shared/components/DomainBadge';
import { renderMarkdown } from '../../../shared/utils/markdownLite';
import { checkAnswer } from '../engine/scorer';
import ExpertTrapBadge from '../../../shared/components/ExpertTrapBadge';

/**
 * Post-exam review mode — THE most important feature.
 * Walk through questions with user answer, correct answer, explanation.
 */
export default function ReviewMode({ examRecord, questions: allQuestions }) {
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter'); // 'wrong', 'flagged', domain ID
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build question list with answers
  const reviewItems = useMemo(() => {
    if (!examRecord) return [];
    const ordered = examRecord.questions
      .map(qId => allQuestions.find(q => q.id === qId))
      .filter(Boolean)
      .map(q => ({
        question: q,
        userAnswer: examRecord.answers[q.id],
        isCorrect: checkAnswer(q, examRecord.answers[q.id]),
        isFlagged: examRecord.flags?.includes(q.id),
        timeSpent: examRecord.time_per_question?.[q.id] || 0,
      }));

    if (filter === 'wrong') return ordered.filter(i => !i.isCorrect);
    if (filter === 'flagged') return ordered.filter(i => i.isFlagged);
    if (filter && DOMAINS.some(d => d.id === filter)) return ordered.filter(i => i.question.domain === filter);
    return ordered;
  }, [examRecord, allQuestions, filter]);

  const current = reviewItems[currentIndex];

  if (!current || reviewItems.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 mb-4">No questions to review with this filter.</p>
        <Link to={`/exam/results/${examRecord?.id}`} className="text-blue-600 hover:text-blue-700">
          ← Back to Results
        </Link>
      </div>
    );
  }

  const { question, userAnswer, isCorrect, isFlagged, timeSpent } = current;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to={`/exam/results/${examRecord.id}`}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          ← Results
        </Link>
        <span className="text-sm text-gray-500">
          Reviewing {currentIndex + 1} of {reviewItems.length}
          {filter && ` (${filter})`}
        </span>
      </div>

      {/* Question */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <DomainBadge domainId={question.domain} size="xs" />
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {isCorrect ? 'Correct' : 'Wrong'}
          </span>
          {isFlagged && (
            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Flagged</span>
          )}
          {timeSpent > 0 && (
            <span className="text-xs text-gray-400">{Math.round(timeSpent)}s</span>
          )}
        </div>

        <div className="text-gray-900 leading-relaxed mb-4">
          {renderMarkdown(question.prompt)}
        </div>

        {/* Show options with correct/wrong highlighting */}
        {question.options && (
          <div className="space-y-2 mb-4">
            {question.options.map(opt => {
              const isSelected = Array.isArray(userAnswer)
                ? userAnswer.includes(opt.id)
                : userAnswer === opt.id;
              const isOptionCorrect = opt.is_correct;

              let borderClass = 'border-gray-200';
              let bgClass = 'bg-white';
              if (isOptionCorrect) { borderClass = 'border-green-500'; bgClass = 'bg-green-50'; }
              if (isSelected && !isOptionCorrect) { borderClass = 'border-red-500'; bgClass = 'bg-red-50'; }

              return (
                <div key={opt.id} className={`flex items-start gap-3 p-3 rounded-lg border-2 ${borderClass} ${bgClass}`}>
                  <span className="font-medium text-gray-400">{opt.id}.</span>
                  <span className="text-gray-900 flex-1">{opt.text}</span>
                  {isOptionCorrect && <span className="text-green-600 text-sm">✓ Correct</span>}
                  {isSelected && !isOptionCorrect && <span className="text-red-600 text-sm">✗ Your answer</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Explanation</h4>
        <div className="text-blue-900 text-sm leading-relaxed">
          {renderMarkdown(question.explanation)}
        </div>
        {question.cisco_reference && (
          <p className="text-xs text-blue-600 mt-2">Reference: {question.cisco_reference}</p>
        )}
      </div>

      {/* Expert trap warning */}
      <ExpertTrapBadge warning={question.expert_trap_warning} className="mb-4" />

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-30"
        >
          ← Previous
        </button>
        <button
          onClick={() => setCurrentIndex(Math.min(reviewItems.length - 1, currentIndex + 1))}
          disabled={currentIndex >= reviewItems.length - 1}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
